/**
 * One audio element per surface. `AudioPlayerProvider` owns it; rows call `usePlayer().toggle(item)`; the
 * `MiniPlayer` shows what is playing (fixed at the bottom of the Audio Studio, inline in the Soundtracks block).
 *
 * Queues: `playQueue(items, startIndex, { shuffle, key })` plays a list (a collection, All sounds) in order — or
 * shuffled, the chosen start first — and moves on by itself when a sound ends; the mini player then shows previous /
 * next. `key` names the list so its Play button can tell it is the one playing (`queueKey`). A plain `toggle` of
 * another sound leaves the queue.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Pause, Play, SkipBack, SkipForward, Volume2, VolumeX, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { GenreTile } from './CoverArt';
import { formatClock } from './format';

export interface PlayableItem {
  /** Stable key — the row compares against it to show its playing state. */
  id: string;
  title: string;
  subtitle?: string | null;
  audioUrl: string | null;
  coverUrl?: string | null;
}

interface PlayerState {
  current: PlayableItem | null;
  isPlaying: boolean;
  position: number;
  duration: number;
  muted: boolean;
  toggle: (item: PlayableItem) => void;
  /**
   * Plays `items` from `startIndex` (items without audio are skipped). `shuffle` randomises the order after the start;
   * with no `startIndex` a shuffled queue starts on a random sound, a plain one on the first.
   */
  playQueue: (items: PlayableItem[], startIndex?: number, opts?: { shuffle?: boolean; key?: string }) => void;
  /** The active queue's key (null = a single sound, or nothing). */
  queueKey: string | null;
  /** Where the current sound sits in the queue, and its length (0 = no queue). */
  queueIndex: number;
  queueLength: number;
  next: () => void;
  previous: () => void;
  pauseResume: () => void;
  seek: (fraction: number) => void;
  setMuted: (m: boolean) => void;
  stop: () => void;
}

const PlayerContext = createContext<PlayerState | null>(null);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [current, setCurrent] = useState<PlayableItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMutedState] = useState(false);
  const [queue, setQueue] = useState<{ items: PlayableItem[]; index: number; key: string | null } | null>(null);
  // The 'ended' listener is attached once; it reads the queue through this ref.
  const queueRef = useRef<typeof queue>(null);

  const load = useCallback((item: PlayableItem) => {
    const audio = audioRef.current;
    if (!audio || !item.audioUrl) return;
    audio.src = item.audioUrl;
    setPosition(0);
    setDuration(0);
    setCurrent(item);
    void audio.play().catch(() => setIsPlaying(false));
  }, []);

  /** Moves the queue to `index` (out of range: stays put, returns false). */
  const playAt = useCallback((index: number): boolean => {
    const q = queueRef.current;
    if (!q || index < 0 || index >= q.items.length) return false;
    const nextQ = { ...q, index };
    queueRef.current = nextQ;
    setQueue(nextQ);
    load(q.items[index]);
    return true;
  }, [load]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      const q = queueRef.current;
      if (!q || !playAt(q.index + 1)) setIsPlaying(false);
    };
    const onTime = () => setPosition(audio.currentTime);
    const onMeta = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
    };
  }, [playAt]);

  // Leaving the page stops the sound.
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const toggle = useCallback((item: PlayableItem) => {
    const audio = audioRef.current;
    if (!audio || !item.audioUrl) return;
    if (current?.id === item.id) {
      if (audio.paused) void audio.play().catch(() => {});
      else audio.pause();
      return;
    }
    // Another sound on its own: the queue is over.
    queueRef.current = null;
    setQueue(null);
    load(item);
  }, [current?.id, load]);

  const playQueue = useCallback((items: PlayableItem[], startIndex?: number, opts: { shuffle?: boolean; key?: string } = {}) => {
    const start = startIndex != null ? items[startIndex] : undefined;
    let list = items.filter((i) => i.audioUrl);
    if (!list.length) return;
    const first = start?.audioUrl ? start : opts.shuffle ? list[Math.floor(Math.random() * list.length)] : list[0];
    if (opts.shuffle) {
      const rest = list.filter((i) => i !== first);
      for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j], rest[i]];
      }
      list = [first, ...rest];
    }
    const q = { items: list, index: list.indexOf(first), key: opts.key ?? null };
    queueRef.current = q;
    setQueue(q);
    load(first);
  }, [load]);

  const next = useCallback(() => { const q = queueRef.current; if (q) playAt(q.index + 1); }, [playAt]);

  /** Back to the start of the sound when it is a few seconds in (or first in the queue), else the one before. */
  const previous = useCallback(() => {
    const q = queueRef.current;
    const audio = audioRef.current;
    if (!q || !audio) return;
    if (audio.currentTime > 3 || q.index === 0) { audio.currentTime = 0; setPosition(0); return; }
    playAt(q.index - 1);
  }, [playAt]);

  const pauseResume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    if (audio.paused) void audio.play().catch(() => {});
    else audio.pause();
  }, []);

  const seek = useCallback((fraction: number) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    audio.currentTime = audio.duration * Math.min(1, Math.max(0, fraction));
  }, []);

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m);
    if (audioRef.current) audioRef.current.muted = m;
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
    setCurrent(null);
    setIsPlaying(false);
    queueRef.current = null;
    setQueue(null);
  }, []);

  const queueKey = queue?.key ?? null;
  const queueIndex = queue?.index ?? 0;
  const queueLength = queue?.items.length ?? 0;
  const value = useMemo<PlayerState>(
    () => ({ current, isPlaying, position, duration, muted, toggle, playQueue, queueKey, queueIndex, queueLength, next, previous, pauseResume, seek, setMuted, stop }),
    [current, isPlaying, position, duration, muted, toggle, playQueue, queueKey, queueIndex, queueLength, next, previous, pauseResume, seek, setMuted, stop],
  );

  return (
    <PlayerContext.Provider value={value}>
      <audio ref={audioRef} preload="none" className="hidden" />
      {children}
    </PlayerContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function usePlayer(): PlayerState {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within AudioPlayerProvider');
  return ctx;
}

/**
 * Space to keep clear at the bottom of a scrolling list while the docked mini player is showing (its ~57px bar plus
 * breathing room), so the last row is never hidden behind it.
 */
export const MINI_PLAYER_INSET_PX = 88;

/** `MINI_PLAYER_INSET_PX` while something is loaded in the player, else 0 (also 0 outside a provider). */
// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function usePlayerInset(): number {
  const ctx = useContext(PlayerContext);
  return ctx?.current ? MINI_PLAYER_INSET_PX : 0;
}

/** Round play/pause button for a row; overlays the cover when one is given, else `fallback` (default: a tile from the title). */
export function PlayButton({ item, size = 'md', coverUrl, fallback }: { item: PlayableItem; size?: 'xs' | 'sm' | 'row' | 'md'; coverUrl?: string | null; fallback?: ReactNode }) {
  const { current, isPlaying, toggle } = usePlayer();
  const active = current?.id === item.id;
  const playing = active && isPlaying;
  const dim = size === 'xs' ? 'h-7 w-7' : size === 'sm' ? 'h-9 w-9' : size === 'row' ? 'h-10 w-10' : 'h-12 w-12';
  const disabled = !item.audioUrl;
  return (
    <button
      type="button"
      onClick={() => toggle(item)}
      disabled={disabled}
      aria-label={playing ? `Pause ${item.title}` : `Play ${item.title}`}
      className={cn('group/play relative shrink-0 overflow-hidden rounded-lg disabled:cursor-not-allowed disabled:opacity-50', dim)}
    >
      {coverUrl ? (
        <img src={coverUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        fallback ?? <GenreTile title={item.title} size="thumb" />
      )}
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-white transition-colors',
          playing ? 'bg-black/55' : active ? 'bg-black/45' : 'bg-black/20 group-hover/play:bg-black/40',
        )}
      >
        {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="ml-0.5 h-4 w-4" aria-hidden />}
      </span>
    </button>
  );
}

/**
 * The persistent mini player. `fixed` = docked at the bottom of the viewport (Audio Studio page), next to the
 * sidebar on desktop; `inline` = a compact bar inside a card (Soundtracks block).
 */
export function MiniPlayer({ variant = 'fixed' }: { variant?: 'fixed' | 'inline' }) {
  const { current, isPlaying, position, duration, muted, pauseResume, seek, setMuted, stop, queueIndex, queueLength, next, previous } = usePlayer();
  if (!current) return null;
  const inQueue = queueLength > 1;
  const skipCls = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#344054] hover:bg-[#f2f4f7] disabled:opacity-40 disabled:hover:bg-transparent';
  const pct = duration ? (position / duration) * 100 : 0;
  return (
    <div
      role="region"
      aria-label="Audio player"
      className={cn(
        'border-[#eaecf0] bg-white/95 backdrop-blur',
        variant === 'fixed'
          ? 'fixed inset-x-0 bottom-0 z-40 border-t lg:left-16'
          : 'mt-3 rounded-xl border',
      )}
    >
      <div className={cn('mx-auto flex items-center gap-3 px-3 py-2 sm:px-5', variant === 'fixed' && 'max-w-[1400px]')}>
        {current.coverUrl ? (
          <img src={current.coverUrl} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" />
        ) : (
          <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md"><GenreTile title={current.title} size="thumb" /></span>
        )}
        {inQueue && (
          <button type="button" onClick={previous} aria-label="Previous" className={skipCls}><SkipBack className="h-4 w-4" aria-hidden /></button>
        )}
        <button
          type="button"
          onClick={pauseResume}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#101828] text-white hover:bg-[#344054]"
        >
          {isPlaying ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="ml-0.5 h-4 w-4" aria-hidden />}
        </button>
        {inQueue && (
          <button type="button" onClick={next} disabled={queueIndex >= queueLength - 1} aria-label="Next" className={skipCls}><SkipForward className="h-4 w-4" aria-hidden /></button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0 truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
              {current.title}
              {current.subtitle && <span className="ml-2 text-[12px] font-normal text-[#667085]">{current.subtitle}</span>}
              {inQueue && <span className="ml-2 text-[11.5px] font-normal tabular-nums text-[#98a2b3]">{queueIndex + 1} of {queueLength}</span>}
            </div>
            <span className="shrink-0 text-[11.5px] tabular-nums text-[#98a2b3]">
              {formatClock(position)} / {formatClock(duration)}
            </span>
          </div>
          <div
            role="slider"
            tabIndex={0}
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - r.left) / r.width);
            }}
            onKeyDown={(e) => {
              if (!duration) return;
              if (e.key === 'ArrowRight') seek((position + 5) / duration);
              if (e.key === 'ArrowLeft') seek((position - 5) / duration);
            }}
            className="mt-1.5 h-1.5 w-full cursor-pointer overflow-hidden rounded-full bg-[#f2f4f7]"
          >
            <div className="h-full rounded-full bg-[#475467]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          aria-label={muted ? 'Unmute' : 'Mute'}
          className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#667085] hover:bg-[#f2f4f7] sm:flex"
        >
          {muted ? <VolumeX className="h-4 w-4" aria-hidden /> : <Volume2 className="h-4 w-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={stop}
          aria-label="Close player"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#667085] hover:bg-[#f2f4f7]"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
